import type { LabResult } from "@/hooks/useLabPanels";

/**
 * Single lab result row with a visual reference-range bar.
 *
 * Visual model:
 *   ─────[==========================]─────
 *        low                       high
 *                     ▼ value
 *
 * - Bar represents 0% → 100% of the visual axis (which spans 0.7×low → 1.3×high)
 * - In-range zone shaded sage-green
 * - Value rendered as a marker dot
 * - Out-of-range value rendered in red and clamped to the bar edges
 *
 * For non-numeric results (value_text), we render the text + reference_text without a bar.
 */
export function LabResultRow({ result }: { result: LabResult }) {
  const hasNumericValue = result.value != null;
  const hasNumericRange = result.reference_low != null && result.reference_high != null;

  const flagTone = flagToTone(result.flag);

  return (
    <div className="border-b border-fh-border last:border-0 py-4">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-fh-ink text-[14px]">{result.test_name}</div>
          <div className="text-[11px] text-fh-gray uppercase tracking-wider mt-0.5">
            {result.test_code}
          </div>
        </div>
        <div className="text-right whitespace-nowrap">
          <div className={`font-serif text-[20px] leading-none ${flagTone.valueColor}`}>
            {formatValue(result)}
            {result.units && (
              <span className="text-[12px] text-fh-gray ml-1 font-sans">{result.units}</span>
            )}
          </div>
          {flagTone.label && (
            <span className={`inline-block text-[10.5px] font-semibold uppercase tracking-wider mt-1 px-1.5 py-0.5 rounded ${flagTone.badgeBg} ${flagTone.badgeText}`}>
              {flagTone.label}
            </span>
          )}
        </div>
      </div>

      {/* Reference range bar (numeric values only) */}
      {hasNumericValue && hasNumericRange && (
        <RangeBar
          value={result.value as number}
          low={result.reference_low as number}
          high={result.reference_high as number}
          flag={result.flag}
        />
      )}

      {/* Reference text for non-numeric values or as fallback caption */}
      <div className="text-[11.5px] text-fh-gray mt-1.5">
        {hasNumericRange ? (
          <>Reference range: {result.reference_low}–{result.reference_high}{result.units ? ` ${result.units}` : ""}</>
        ) : result.reference_text ? (
          <>Reference: {result.reference_text}</>
        ) : null}
      </div>
    </div>
  );
}

function formatValue(r: LabResult): string {
  if (r.value != null) {
    // Show up to 2 decimal places but strip trailing zeros for cleanliness
    const n = Number(r.value);
    return n % 1 === 0 ? n.toFixed(0) : n.toFixed(2).replace(/\.?0+$/, "");
  }
  return r.value_text ?? "—";
}

interface FlagDisplay {
  valueColor: string;
  badgeBg: string;
  badgeText: string;
  label: string | null;
}

function flagToTone(flag: LabResult["flag"]): FlagDisplay {
  switch (flag) {
    case "low":
      return { valueColor: "text-amber-700", badgeBg: "bg-amber-50",  badgeText: "text-amber-800", label: "Low" };
    case "high":
      return { valueColor: "text-amber-700", badgeBg: "bg-amber-50",  badgeText: "text-amber-800", label: "High" };
    case "critical_low":
      return { valueColor: "text-red-700",   badgeBg: "bg-red-50",    badgeText: "text-red-700",   label: "Critical low" };
    case "critical_high":
      return { valueColor: "text-red-700",   badgeBg: "bg-red-50",    badgeText: "text-red-700",   label: "Critical high" };
    case "abnormal":
      return { valueColor: "text-amber-700", badgeBg: "bg-amber-50",  badgeText: "text-amber-800", label: "Abnormal" };
    default:
      return { valueColor: "text-fh-green-dark", badgeBg: "", badgeText: "", label: null };
  }
}

/**
 * The reference-range bar.
 * - Visual axis spans 0.7×low to 1.3×high (with sensible floor of 0)
 * - Reference range zone is shaded sage-green
 * - Value marker is a vertical line + dot, color-coded by flag
 */
function RangeBar({
  value, low, high, flag,
}: { value: number; low: number; high: number; flag: LabResult["flag"] }) {
  const axisMin = Math.max(0, low * 0.7);
  const axisMax = high * 1.3;
  const axisSpan = Math.max(1, axisMax - axisMin);

  const pct = (v: number) => Math.max(0, Math.min(100, ((v - axisMin) / axisSpan) * 100));

  const lowPct   = pct(low);
  const highPct  = pct(high);
  const valuePct = pct(value);

  const isOutOfRange = flag != null;
  const markerColor = flag === "critical_low" || flag === "critical_high"
    ? "bg-red-600"
    : isOutOfRange
    ? "bg-amber-500"
    : "bg-fh-green-dark";

  return (
    <div className="relative my-2.5">
      {/* Track */}
      <div className="relative h-2 rounded-full bg-fh-border overflow-visible">
        {/* In-range zone */}
        <div
          className="absolute top-0 h-2 rounded-full bg-fh-sage border-y border-fh-green-light/40"
          style={{ left: `${lowPct}%`, width: `${Math.max(0, highPct - lowPct)}%` }}
        />
        {/* Value marker */}
        <div
          className={`absolute top-[-4px] w-1 h-4 rounded-sm ${markerColor}`}
          style={{ left: `calc(${valuePct}% - 2px)` }}
          aria-label={`Value: ${value}`}
        />
        <div
          className={`absolute top-[-7px] w-2.5 h-2.5 rounded-full border-2 border-white ${markerColor}`}
          style={{ left: `calc(${valuePct}% - 5px)` }}
        />
      </div>
      {/* Axis labels */}
      <div className="flex justify-between text-[10px] text-fh-gray-light mt-1">
        <span>{Number(axisMin.toFixed(1))}</span>
        <span className="text-fh-gray font-semibold">{low}</span>
        <span className="text-fh-gray font-semibold">{high}</span>
        <span>{Number(axisMax.toFixed(1))}</span>
      </div>
    </div>
  );
}
